import { useCallback, useEffect, useState } from "react";
import { format, parseISO } from "date-fns";
import { toast } from "sonner";
import { Ban, Check, Rabbit, Stamp, Undo2 } from "lucide-react";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Calendar } from "@/components/ui/calendar";
import { api, errorText } from "@/lib/api";

const MONTHS = ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"];

const AddMember = ({ onAdded }) => {
  const [f, setF] = useState({ name: "", email: "", phone: "", birthday_month: "" });
  const [busy, setBusy] = useState(false);
  const set = (k) => (e) => setF((x) => ({ ...x, [k]: e?.target ? e.target.value : e }));
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const r = await api.post("/admin/members", { ...f, birthday_month: Number(f.birthday_month) || null });
      toast.success(`${r.data.name} is in the Lounge — ${r.data.member_no}. Access code: ${r.data.code}. Give it to her!`);
      setF({ name: "", email: "", phone: "", birthday_month: "" });
      onAdded();
    } catch (e2) { toast.error(errorText(e2)); } finally { setBusy(false); }
  };
  return (
    <form className="owner-card vip-add" onSubmit={submit} data-testid="vip-add-form">
      <div className="st-grid2">
        <label className="st-label">Name *<input className="st-control" required value={f.name} onChange={set("name")} data-testid="vip-add-name" /></label>
        <label className="st-label">Email *<input className="st-control" type="email" required value={f.email} onChange={set("email")} data-testid="vip-add-email" /></label>
      </div>
      <div className="st-grid2">
        <label className="st-label">Phone<input className="st-control" value={f.phone} onChange={set("phone")} data-testid="vip-add-phone" /></label>
        <div className="st-label">Birthday month
          <select className="st-control" value={f.birthday_month} onChange={set("birthday_month")} data-testid="vip-add-month">
            <option value="">—</option>{MONTHS.map((m, i) => <option key={m} value={i + 1}>{m}</option>)}
          </select>
        </div>
      </div>
      <button className="cta sm" disabled={busy} data-testid="vip-add-submit">{busy ? "Crowning…" : "Crown This Bunny"}</button>
      <p className="owner-hint">Add a client after her <b>second completed appointment</b> — the House creates her Black Card and you share her access code so she can sign in on the VIP page.</p>
    </form>
  );
};

const MemberCard = ({ m, onChanged }) => {
  const [open, setOpen] = useState(false);
  const [amount, setAmount] = useState("55");
  const [note, setNote] = useState("");
  const thisMonth = new Date().getMonth() + 1;
  const thisYear = new Date().getFullYear();

  const addStamp = async () => {
    try {
      const r = await api.post(`/admin/members/${m.id}/stamps`, { amount: Number(amount), note: note.trim() || `Qualifying appointment ($${amount})` });
      toast.success(`Stamp added.${r.data.issued.length ? ` 🎉 ${r.data.issued.map((k) => `$${k.value} Kash issued!`).join(" ")}` : ""}`);
      setNote(""); onChanged();
    } catch (e) { toast.error(errorText(e)); }
  };
  const reverse = async (entryId) => {
    if (!window.confirm("Reverse this stamp? The history stays on file.")) return;
    try { await api.delete(`/admin/members/${m.id}/stamps/${entryId}`); toast.success("Stamp reversed."); onChanged(); }
    catch (e) { toast.error(errorText(e)); }
  };
  const redeem = async (k) => {
    const ref = window.prompt("Redeemed with what? (booking date or note)");
    if (!ref) return;
    try { await api.post(`/admin/kash/${k.id}/redeem`, { ref }); toast.success("Marked redeemed."); onChanged(); }
    catch (e) { toast.error(errorText(e)); }
  };
  const voidKash = async (k) => {
    if (!window.confirm(`Void ${k.code} ($${k.value})?`)) return;
    try { await api.post(`/admin/kash/${k.id}/void`); toast.success("Voucher voided."); onChanged(); }
    catch (e) { toast.error(errorText(e)); }
  };
  const birthdayUsed = async () => {
    try {
      await api.post(`/admin/members/${m.id}/birthday`, { year: thisYear });
      toast.success(`Birthday privilege marked used for ${thisYear}.`); onChanged();
    } catch (e) { toast.error(errorText(e)); }
  };

  return (
    <article className="owner-card vip-member" data-testid={`vip-member-${m.member_no}`}>
      <div className="bk-top">
        <div>
          <p className="bk-when">{m.member_no} · cycle {m.cycle_number} · {m.total_stamps} lifetime stamps</p>
          <h3>{m.name}</h3>
          <p className="bk-meta">{m.email} · {m.phone || "no phone"} · code <b>{m.code}</b>
            {m.birthday_month ? ` · birthday ${MONTHS[m.birthday_month - 1]}` : ""}</p>
        </div>
        <button type="button" className="cta ghost sm" onClick={() => setOpen(!open)} data-testid={`vip-history-toggle-${m.member_no}`}>
          {open ? "Hide" : "Manage"}
        </button>
      </div>
      <p className="vip-stamp-line"><Stamp size={14} /> {m.stamps_cycle}/10 stamps this cycle
        {m.stamps_cycle >= 10 ? " — card renewed!" : m.stamps_cycle >= 5 ? " — next: $30 at 10" : m.stamps_cycle >= 3 ? " — next: $15 at 5" : " — next: $5 at 3"}</p>
      {open ? (
        <div className="vip-manage">
          <div className="vip-stamp-row">
            <input className="st-control" type="number" min="55" value={amount} onChange={(e) => setAmount(e.target.value)}
              aria-label="Appointment total" data-testid={`vip-stamp-amount-${m.member_no}`} />
            <input className="st-control" placeholder="Note (e.g. 'Oct 12 retwist')" value={note}
              onChange={(e) => setNote(e.target.value)} maxLength={160} data-testid={`vip-stamp-note-${m.member_no}`} />
            <button type="button" className="cta sm" onClick={addStamp} data-testid={`vip-add-stamp-${m.member_no}`}>
              <Stamp size={14} /> Add stamp
            </button>
          </div>
          {m.kash.length ? (
            <div className="vip-kash-admin">
              {m.kash.map((k) => (
                <div key={k.id} className={`my-kash-item ${k.status}`} data-testid={`vip-kash-${k.id}`}>
                  <b>${k.value}</b><span>{k.code}</span>
                  <em>{k.status === "issued" ? "issued" : k.status === "redeemed" ? `redeemed · ${k.redeemed_ref}` : k.status}</em>
                  {k.status === "issued" ? (
                    <span className="vip-kash-actions">
                      <button type="button" className="cta ghost sm" onClick={() => redeem(k)} data-testid={`vip-redeem-${k.id}`}><Check size={13} /> Redeemed</button>
                      <button type="button" className="icon-btn" onClick={() => voidKash(k)} aria-label="Void voucher" data-testid={`vip-void-${k.id}`}><Ban size={14} /></button>
                    </span>
                  ) : null}
                </div>
              ))}
            </div>
          ) : <p className="owner-hint">No Smiless Kash yet — first voucher at stamp 3.</p>}
          {m.birthday_month ? (
            <div className="vip-bday-row">
              <span>Birthday privilege {thisYear}: {m.birthday_used.includes(thisYear) ? "used" : thisMonth === m.birthday_month ? "available THIS MONTH" : `opens ${MONTHS[m.birthday_month - 1]}`}</span>
              {thisMonth === m.birthday_month && !m.birthday_used.includes(thisYear) ? (
                <button type="button" className="cta ghost sm" onClick={birthdayUsed} data-testid={`vip-birthday-used-${m.member_no}`}>Mark used</button>
              ) : null}
            </div>
          ) : null}
          <details className="vip-history" data-testid={`vip-ledger-${m.member_no}`}><summary>Membership history</summary>
            <ul>{(m.ledger || []).map((l) => (
              <li key={l.id}>{format(parseISO(l.created_at), "MMM d")} — {l.note}
                {l.kind === "stamp" && !l.reversed ? (
                  <button type="button" className="icon-btn" onClick={() => reverse(l.id)} aria-label="Reverse stamp"
                    data-testid={`vip-reverse-${l.id}`}><Undo2 size={13} /></button>) : null}
              </li>
            ))}</ul>
          </details>
        </div>
      ) : null}
    </article>
  );
};

export const VipTab = () => {
  const [members, setMembers] = useState(null);
  const load = useCallback(() => api.get("/admin/members").then((r) => setMembers(Array.isArray(r.data) ? r.data : []))
    .catch(() => setMembers([])), []);
  useEffect(() => { load(); }, [load]);
  return (
    <section>
      <p className="owner-hint"><Rabbit size={15} /> Stamps are added by you after verifying each completed qualifying appointment ($55 minimum). Cancelled, missed or incomplete visits never earn stamps. Rewards issue automatically at 3, 5 and 10 stamps.</p>
      <AddMember onAdded={load} />
      {members === null ? <p className="owner-empty">Loading…</p> : !members.length ? (
        <p className="owner-empty" data-testid="vip-empty">No VIP members yet — the Lounge fills after your first rebooked queen.</p>
      ) : members.map((m) => <MemberCard key={m.id} m={m} onChanged={load} />)}
    </section>
  );
};
